import { ResponseStatus, WidgetStyle } from '@sdbn/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Pager } from '../model/pager';
import { PagereService } from '../service/pager.service';
import { PagerDetails } from '../model/pager-details';
import { TranslationService } from '@sdbn/common';
import { MatDialog } from '@angular/material/dialog';
import { ResponseMessage } from '@sdbn/common';
import { NgForm } from '@angular/forms';
import { ContextChange } from '@sdbn/common';
import { MasterDetailListEntry } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/common';
import { EquipmentIdentifier, EquipmentType } from '@sdbn/tmc-common';
import { ConfirmDialogComponent, ConfirmDialogData } from '@sdbn/tmc-legacy';

@Component({
    selector: 'app-pager',
    templateUrl: './pager.component.html',
    styleUrls: ['./pager.component.scss'],
    standalone: false
})
export class PagerComponent extends AbstractWidgetComponent implements OnInit {

  pagers: Pager[] = [];

  equipmentIdentifier: EquipmentIdentifier;

  selectedPager: Pager;
  selectedDetails?: PagerDetails;
  workingCopyDetails?: PagerDetails;

  responseMessages: ResponseMessage[];
  isLoading = false;

  @ViewChild('pagerForm') form: NgForm;

  get cancelEnabled() {
    return !this.isLoading && this.form != null && this.form.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.form != null && this.form.dirty && this.form.valid;
  }

  constructor(
    public translationService: TranslationService,
    private pagerService: PagereService,
    private dialog: MatDialog) {
    super();
  }

  ngOnInit() {
    this.load();
  }

  setSelected(pager: Pager) {

    if (this.selectedPager != null && this.selectedPager.id === pager.id) {
      return;
    }

    if (this.form?.form?.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.pager.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.pager.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(pager);
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(pager);
    }


  }

  setSelectedWithoutCheck(pager: Pager) {

    this.setLoading(true);
    this.responseMessages = [];
    if (pager != null) {

      this.equipmentIdentifier = new EquipmentIdentifier({
        id: pager.id,
        typ: EquipmentType.Pager
      })

      this.pagerService.getPagerDetails({ id: pager.id }).subscribe(details => {
        this.selectedPager = pager;
        this.selectedDetails = details;
        this.workingCopyDetails = JSON.parse(JSON.stringify(details));
        this.form.resetForm(this.workingCopyDetails);
        this.setLoading(false);
      }, error => {
        this.setLoading(false);
        console.log(error);
      });
    } else {
      this.setLoading(false);
    }
  }

  isSelected(details: Pager) {
    return this.selectedDetails != null && this.selectedDetails.id === details.id;
  }

  delete(pager: Pager) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.ui.admin.pager.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.ui.admin.pager.delete.confirm-dialog.message',
      confirm: () => {
        this.setLoading(true);
        this.pagerService.deletePager({ id: pager.id }).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.setLoading(false);
        }, error => {
          console.log(error);
          this.setLoading(false);
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  load() {
    this.setLoading(true);
    this.responseMessages = [];
    this.pagerService.getPagers().subscribe(pagers => {

      let newPagers = new Array<Pager>();
      for (let pager of pagers) {
        newPagers.push(new Pager(pager));
      }

      this.pagers = newPagers;

      if (newPagers.length > 0) {
        if (this.selectedPager == null) {
          this.setSelectedWithoutCheck(newPagers[0]);
        } else {
          this.setSelectedWithoutCheck(this.pagers.find(s => s.id === this.selectedPager.id));
        }
      }

      this.setLoading(false);
    }, error => {
      console.error(error);
      this.setLoading(false);
    });
  }

  addNew() {
    console.log('adding new');
    if (this.form.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.pager.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.pager.discard.confirm-dialog.message',
        confirm: () => {
          this.selectedDetails = null;
          this.clear();
          this.workingCopyDetails = new PagerDetails();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.selectedDetails = null;
      this.clear();
      this.workingCopyDetails = new PagerDetails();
    }
  }

  cancel() {
    if (this.form.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.pager.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.pager.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    this.form.resetForm(this.form.value);
    if (this.workingCopyDetails.id != null) {
      this.setSelectedWithoutCheck(this.selectedPager);
    } else {
      this.workingCopyDetails = new PagerDetails();
      this.workingCopyDetails.available = true;
      this.selectedPager = null;
    }
  }

  clear() {
    this.form.resetForm(this.form.value);
    this.responseMessages = [];
    this.selectedPager = null;
    this.selectedDetails = new PagerDetails();
    this.selectedDetails.available = true;
    this.workingCopyDetails = JSON.parse(JSON.stringify(this.selectedDetails));
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  save() {
    if (this.pagers.some(s => {
      return (this.selectedPager == null && s.nbr === this.workingCopyDetails.nbr) ||
        (this.selectedPager != null && s.id !== this.selectedPager.id && s.nbr === this.workingCopyDetails.nbr);
    }
    )) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.pager.save-duplicate.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.pager.save-duplicate.confirm-dialog.message',
        confirm: () => {
          this.saveWithoutCheck();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.saveWithoutCheck();
    }
  }

  saveWithoutCheck() {
    this.setLoading(true);
    this.responseMessages = [];
    if (this.workingCopyDetails.id != null) {
      this.updateDetails(this.workingCopyDetails);
    } else {
      this.createDetails(this.workingCopyDetails);
    }
  }

  updateDetails(detailsToUpdate: PagerDetails) {
    this.pagerService.savePager(detailsToUpdate).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.form.resetForm(this.form.value);
        this.selectedDetails = JSON.parse(JSON.stringify(this.workingCopyDetails));
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  createDetails(detailsToAdd: PagerDetails) {
    this.pagerService.createPager(detailsToAdd).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.clearAndLoad();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as Pager);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as Pager);
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
